import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import BuildIcon from "@mui/icons-material/Build";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import "./NotificationPanel.css";

  const BASE_URL = "https://estbn.com/EstbnNew/public/api";

  const getNotificationIcon = (type) => {
    switch (type) {
      case "cart_notification":
        return <ShoppingCartIcon fontSize="small" />;
      case "price_plan_notification":
        return <LocalOfferIcon fontSize="small" />;
      case "other_services_notification":
        return <BuildIcon fontSize="small" />;
      default:
        return <NotificationsNoneIcon fontSize="small" />;
    }
  };

  const getTimeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const NotificationPanel = () => {

  const { user } = useSelector((state) => state.auth);
  const token = user?.token;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(
    async (filter = "all") => {
      if (!token) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/notifications?per_page=5&filter=${filter}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        const json = await res.json();
        if (json.success) {
          setNotifications(json.data.notifications);
          setUnreadCount(json.data.counts.unread);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (open) fetchNotifications(activeTab);
  }, [open, activeTab, fetchNotifications]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const markOneRead = async (id) => {
    if (!token) return;
    try {
      await fetch(`${BASE_URL}/notifications/${id}/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error(err);
    }
  };

  const markAllRead = async () => {
    if (!token) return;
    try {
      await fetch(`${BASE_URL}/notifications/read-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read_at) markOneRead(notification.id);
    if (notification.data?.data?.link) {
      navigate(notification.data.data.link);
      setOpen(false);
    }
  };

  const handleShowAll = () => {
    navigate("/notifications");
    setOpen(false);
  };

  useEffect(() => {
  if (!token) return;
  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/notifications?per_page=1&filter=unread`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      const json = await res.json();
      if (json.success) {
        setUnreadCount(json.data.counts.unread);
      }
    } catch (err) {
      console.error("Polling failed:", err);
    }
  };
  fetchUnreadCount();
  const interval = setInterval(fetchUnreadCount, 30000);
  return () => clearInterval(interval);
  }, [token]);

  return (
    <div className="notif-wrapper" ref={panelRef}>
      <button
        className="notif-bell-btn"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Notifications"
      >
        <NotificationsIcon />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-panel-header">
            <span className="notif-panel-title">Notifications</span>
            <button
              className="notif-mark-all-btn"
              onClick={markAllRead}
              disabled={unreadCount === 0}
            >
              Mark all as read
            </button>
          </div>

          <div className="notif-tabs">
            <div className="notif-tabs-left">
              <button
                className={`notif-tab ${activeTab === "all" ? "active" : ""}`}
                onClick={() => setActiveTab("all")}
              >
                All
              </button>
              <button
                className={`notif-tab ${activeTab === "unread" ? "active" : ""}`}
                onClick={() => setActiveTab("unread")}
              >
                Unread
                {unreadCount > 0 && (
                  <span className="notif-unread-chip">{unreadCount}</span>
                )}
              </button>
            </div>
            <button className="notif-settings-btn" title="Settings">
              <SettingsOutlinedIcon fontSize="small" />
              <span>Settings</span>
            </button>
          </div>

          <div className="notif-list">
            {loading ? (
              <div className="notif-empty">
                <div className="notif-skeleton-list">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="notif-skeleton-item">
                      <div className="notif-skeleton-icon" />
                      <div className="notif-skeleton-body">
                        <div className="notif-skeleton-line short" />
                        <div className="notif-skeleton-line long" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notif-empty">
                <NotificationsNoneIcon className="notif-empty-icon" />
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((n) => {
                const isUnread = !n.read_at;
                return (
                  <div
                    key={n.id}
                    className={`notif-item ${isUnread ? "unread" : "read"}`}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className={`notif-item-icon-wrap ${n.type}`}>
                      {getNotificationIcon(n.type)}
                    </div>
                    <div className="notif-item-content">
                      <div className="notif-item-title">{n.data?.title}</div>
                      <div className="notif-item-body">{n.data?.body}</div>
                      <div className="notif-item-time text-end">{getTimeAgo(n.created_at)}</div>
                    </div>
                    {isUnread && (
                      <span className="notif-item-dot">
                        <FiberManualRecordIcon style={{ fontSize: 10 }} />
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="notif-footer">
            <button className="notif-show-all-btn" onClick={handleShowAll}>
              Show All
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
